import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { mrbAPI } from '@/lib/api';

export const MAX_PHOTOS = 5;
export const photoIds = (photos) => (photos || []).map((p) => p.id);

// Optional defect photos uploaded before the quarantine is saved; parent sends photoIds(value) as photo_ids.
export const QuarantinePhotos = ({ value = [], onChange, disabled, testid = 'quarantine-photos' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const room = MAX_PHOTOS - value.length;
    if (files.length > room) toast.error(`Up to ${MAX_PHOTOS} photos allowed`);
    setBusy(true);
    const added = [];
    for (const file of files.slice(0, room)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await mrbAPI.stagePhoto(fd);
        added.push({ id: res.data.id, name: file.name, url: URL.createObjectURL(file) });
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setBusy(false);
    if (added.length) onChange([...value, ...added]);
  };

  return (
    <div data-testid={testid}>
      <Label className="text-xs">Defect photos <span className="text-muted-foreground">(optional, up to {MAX_PHOTOS})</span></Label>
      <div className="flex flex-wrap gap-2 mt-1">
        {value.map((p, i) => (
          <div key={p.id} className="relative w-16 h-16 rounded border overflow-hidden bg-slate-50" data-testid={`${testid}-thumb-${i}`}>
            <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
            {!disabled && (
              <button type="button" onClick={() => onChange(value.filter((x) => x.id !== p.id))}
                className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
                data-testid={`${testid}-remove-${i}`}><X className="w-3 h-3" /></button>
            )}
          </div>
        ))}
        {value.length < MAX_PHOTOS && !disabled && (
          <Button type="button" variant="outline" className="w-16 h-16 flex-col gap-1 text-[10px]" disabled={busy}
            onClick={() => inputRef.current?.click()} data-testid={`${testid}-add-btn`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {busy ? 'Uploading' : 'Add photo'}
          </Button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={addFiles} data-testid={`${testid}-input`} />
      <p className="text-[10px] text-muted-foreground mt-0.5">Photos are attached to the MRB case so the board sees the defect straight away.</p>
    </div>
  );
};
